import { FC, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { CarDriverNoCarDepency } from "../../http/carDrivers/types";
import { useCarsApi } from "../../http/cars/api";
import { getTypesOfUse } from "../../http/cars/types";
import { NestedPick, PaginationQueryWithFields } from "../../types";
import { formatDateNumeric } from "../../types/utils";
import Header from "../ui/Header";
import Panel from "../ui/Panel";
import Table from "../ui/Table";

const CAR_DRIVER_FIELDS = [
  "id",
  "car.id",
  "car.vehicleIdentificationNumber",
  "car.currentPlateNumber",
  "car.lastPlateNumber",
  "car.manufacturer.id",
  "car.manufacturer.name",
  "car.model",
  "car.hp",
  "driver.id",
  "driver.fullName",
  "startDate",
  "endDate",
  "typeOfUse",
  "versionNumber",
] as const;
type CarDriverField = (typeof CAR_DRIVER_FIELDS)[number];
type CarDriverSearch = NestedPick<CarDriverNoCarDepency, CarDriverField>;

interface MyCarsProps {
  isTeam?: boolean;
}

const MyCars: FC<MyCarsProps> = ({ isTeam }: MyCarsProps) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const navigate = useNavigate();
  const api = useCarsApi();

  const typesOfUse = getTypesOfUse(i18n.t);

  const filters = useListFilters({
    isSearchable: true,
  });
  const debouncedValue = useDebounce(filters.state.q);

  const query: PaginationQueryWithFields<CarDriverSearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: CAR_DRIVER_FIELDS,
  };

  const result = api.useMyCars(query, aborter.current.signal, isTeam);

  return (
    <>
      <Header title={isTeam ? i18n.t.common.myTeamCars : i18n.t.common.myCars} />
      <Panel>
        <Table<CarDriverSearch>
          filters={filters}
          searchOptions={{
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          head={
            <>
              <th align="left">{i18n.t.plateNumbers.singular}</th>
              <th align="left">{i18n.t.common.carVin}</th>
              <th align="left">{i18n.t.manufacturers.singular}</th>
              <th align="left">{i18n.t.common.model}</th>
              <th align="left">{i18n.t.common.engineCapacity}</th>
              {isTeam && <th align="left">{i18n.t.drivers.singular}</th>}
              <th align="left">{i18n.t.common.typeOfUse}</th>
              <th align="left">{i18n.t.common.startDate}</th>
              <th align="left">{i18n.t.common.endDate}</th>
            </>
          }
          renderRow={(item) => (
            <>
              <td>{item.car.currentPlateNumber || item.car.lastPlateNumber}</td>
              <td>{item.car.vehicleIdentificationNumber}</td>
              <td>{item.car.manufacturer.name}</td>
              <td>{item.car.model}</td>
              <td>{item.car.hp}</td>
              {isTeam && <td>{item.driver.fullName}</td>}
              <td>{typesOfUse.find((x) => x.value)?.label}</td>
              <td>{formatDateNumeric(item.startDate, i18n.language)}</td>
              <td>{item.endDate ? formatDateNumeric(item.endDate, i18n.language) : ""}</td>
            </>
          )}
          onRowClick={(item) => navigate((isTeam ? "/my_team_cars" : "/my_cars") + `/${item.car.id}/${item.id}`)}
          items={result?.data?.items}
          error={result.error}
          total={result.data?.total}
        />
      </Panel>
    </>
  );
};

export default MyCars;
